import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Github, ExternalLink } from 'lucide-react';
import { VERSION_INFO, features } from '@/lib/version';

export function VersionInfo() {
  const [isOpen, setIsOpen] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2">
          <Info className="h-4 w-4" />
          <span className="sr-only">Informações da versão</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {VERSION_INFO.name}
            <Badge variant="secondary">v{VERSION_INFO.version}</Badge>
          </DialogTitle>
          <DialogDescription>
            {VERSION_INFO.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Build Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Informações de Build</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Versão:</span>
                <code className="bg-muted px-1 rounded">{VERSION_INFO.version}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ambiente:</span>
                <Badge variant={VERSION_INFO.environment === 'production' ? 'default' : 'secondary'}>
                  {VERSION_INFO.environment}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Build:</span>
                <span className="text-xs font-mono">
                  {formatDate(VERSION_INFO.buildDate)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Funcionalidades Ativas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(features).map(([key, enabled]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-xs text-muted-foreground">
                      {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Current Version Changelog */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Novidades v{VERSION_INFO.version}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {VERSION_INFO.changelog[VERSION_INFO.version as keyof typeof VERSION_INFO.changelog]?.slice(0, 4).map((item, index) => (
                  <li key={index} className="flex gap-2">
                    <span className="text-green-500">•</span>
                    <span>{item}</span>
                  </li>
                ))}
                {VERSION_INFO.changelog[VERSION_INFO.version as keyof typeof VERSION_INFO.changelog]?.length > 4 && (
                  <li className="flex gap-2 text-blue-600">
                    <span>•</span>
                    <span>E mais...</span>
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>

          {/* Links */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" className="flex-1" asChild>
              <a href="https://github.com/matsnascimento/zelofin" target="_blank" rel="noopener noreferrer">
                <Github className="h-4 w-4 mr-1" />
                GitHub
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setIsOpen(false)}>
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VersionInfo;
